import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ArrowBack, Link as LinkIcon } from "@mui/icons-material"
import { Alert, Box, Button, Skeleton, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { NotFoundError } from "api/errors"
import { useAppDispatch } from "hooks/redux"
import { useNotifications } from "hooks/notifications"
import { useCopyToClipboard } from "hooks/use-copy-to-clipboard"
import { useAccountAlias, useIsScheduledWorkflowEmailResultsEnabled, useUserId } from "resources/account"
import { useMarkScheduledWorkflowDeliverableRead, useScheduledWorkflowDeliverable } from "resources/scheduled-workflows/hooks"
import { buildScheduledWorkflowFileUrl } from "resources/scheduled-workflows/routes"
import type { ScheduledWorkflowFileUrlKind } from "resources/scheduled-workflows/routes"
import type { ScheduledWorkflowDeliverableFile, ScheduledWorkflowListItem } from "resources/scheduled-workflows/types"
import { closeDockPane, setSurfaceView, setWorkflowToOpen } from "../store/agent-chat-slice"
import type { ArtifactEnvelope, FileArtifact } from "../chatbox/artifact-contract"
import { ArtifactPanelBody } from "../chatbox/artifact-renderers"
import { ArtifactShareButton } from "../chatbox/artifact-share-button"
import type { ShareSource } from "../chatbox/resource-sharing"
import { reportUrl, workflowUrl } from "../chatbox/resource-sharing"
import { useKellyLinkNavigate } from "../kelly-link-types"
import { useArtifactDownload } from "../chatbox/use-artifact-download"
import { runOutcomeWord } from "./latest-report"
import { primaryFileOf } from "./report-file"
import { reportAccessOf, reportEntryLabel, reportSourceLine } from "./report-format"
import { ReportEmailSubscription } from "./report-email-subscription"
import { RunHistory } from "./reports-section"
import { RowActions } from "./row-actions"
import type { ShareableWorkflow } from "./use-workflow-share-launcher"
import { formatReportTime, viewerTimeZone } from "./schedule-format"
import { useNow } from "./use-now"

const CONTEXT_SX = { display: "flex", alignItems: "center", gap: 1, pb: 1.5 } satisfies SxProps<Theme>
const SOURCE_SX = { flex: 1, minWidth: 0, color: "text.secondary" } satisfies SxProps<Theme>
const WORKFLOW_NAME_SX = { color: "text.primary" } satisfies SxProps<Theme>
const FILE_DOWNLOAD_SX = { p: 0, minWidth: 0, textTransform: "none", fontWeight: "normal" } satisfies SxProps<Theme>
const VIEW_SX = { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 1 } satisfies SxProps<Theme>
const VIEW_HEADER_SX = { display: "flex", alignItems: "center", gap: 1 } satisfies SxProps<Theme>
const VIEW_TITLE_SX = { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } satisfies SxProps<Theme>
const VIEW_BODY_SX = { display: "flex", flex: 1, minHeight: 0, gap: 2 } satisfies SxProps<Theme>
const VIEW_REPORT_SX = { flex: 1, minWidth: 0, overflow: "auto" } satisfies SxProps<Theme>
const VIEW_RUNS_SX = { width: 240, flexShrink: 0, overflow: "auto" } satisfies SxProps<Theme>
const PARTIAL_LABEL_SX = { color: "error.main", pb: 1 } satisfies SxProps<Theme>
const LATEST_REPORT_SX = { pb: 1 } satisfies SxProps<Theme>
const IGNORE = () => undefined
const IGNORE_MARK_READ_ERROR = () => null
const REPORT_SKELETON_SX = { flex: 1, minHeight: "10rem" } satisfies SxProps<Theme>
const FILE_SHARE_ROW_SX = { display: "flex", justifyContent: "flex-end" } satisfies SxProps<Theme>
const OTHER_FILE_SX = { display: "flex", alignItems: "center", gap: 0.5 } satisfies SxProps<Theme>
const OTHER_FILES_SX = { display: "flex", flexDirection: "column", gap: 0.5, pt: 1.5, m: 0, pl: 0, listStyle: "none" } satisfies SxProps<Theme>

function reportFileArtifact(deliverableId: string, file: ScheduledWorkflowDeliverableFile): FileArtifact {
  return { id: deliverableId, type: "file", filename: file.filename, contentType: file.contentType, deliverable: { deliverableId, fileId: file.fileId } }
}

export const REPORT_PANEL_NAME = "Report"

export function reportArtifactOf(deliverableId: string): ArtifactEnvelope {
  // no filename: there is no file to name, so isFileArtifact keeps this out of the file renderer
  return { id: deliverableId, type: "file", title: REPORT_PANEL_NAME, deliverable: { deliverableId } }
}

function useReportFileSource(deliverableId: string, file: ScheduledWorkflowDeliverableFile) {
  const accountAlias = useAccountAlias()
  const artifact = useMemo(() => reportFileArtifact(deliverableId, file), [deliverableId, file])
  const urlFor = useCallback(
    (kind: ScheduledWorkflowFileUrlKind) => buildScheduledWorkflowFileUrl({ accountAlias, deliverableId, fileId: file.fileId }, kind),
    [accountAlias, deliverableId, file.fileId],
  )
  return { artifact, urlFor }
}

interface ReportFileShareProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
}

function ReportFileShare({ deliverableId, file }: ReportFileShareProps) {
  const source = useMemo<ShareSource>(() => ({ deliverableId, fileId: file.fileId }), [deliverableId, file.fileId])
  return <ArtifactShareButton source={source} artifactName={file.filename} ariaLabel={`Share ${file.filename}`} />
}

interface ReportFileProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
  isExpanded: boolean
  canShare: boolean
}

function ReportFile({ deliverableId, file, isExpanded, canShare }: ReportFileProps) {
  const { artifact, urlFor } = useReportFileSource(deliverableId, file)
  return (
    <Box sx={VIEW_SX}>
      {canShare && (
        <Box sx={FILE_SHARE_ROW_SX}>
          <ReportFileShare deliverableId={deliverableId} file={file} />
        </Box>
      )}
      <ArtifactPanelBody artifact={artifact} isExpanded={isExpanded} threadId={null} urlFor={urlFor} />
    </Box>
  )
}

interface ReportFileDownloadProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
}

function ReportFileDownload({ deliverableId, file }: ReportFileDownloadProps) {
  const { artifact, urlFor } = useReportFileSource(deliverableId, file)
  const { downloadArtifact, isDownloading } = useArtifactDownload(artifact, null, null, urlFor)
  const handleDownload = useCallback(() => downloadArtifact(null, deliverableId), [downloadArtifact, deliverableId])
  return (
    <Button variant="text" size="small" aria-label={`Download ${file.filename}`} disabled={isDownloading} onClick={handleDownload} sx={FILE_DOWNLOAD_SX}>
      {file.filename}
    </Button>
  )
}

interface OtherFilesProps {
  deliverableId: string
  files: ScheduledWorkflowDeliverableFile[]
  canShare: boolean
}

function OtherFiles({ deliverableId, files, canShare }: OtherFilesProps) {
  if (files.length === 0) return null
  return (
    <Box component="ul" sx={OTHER_FILES_SX}>
      {files.map((file) => (
        <Box component="li" key={file.fileId} sx={OTHER_FILE_SX}>
          <ReportFileDownload deliverableId={deliverableId} file={file} />
          {canShare && <ReportFileShare deliverableId={deliverableId} file={file} />}
        </Box>
      ))}
    </Box>
  )
}

function useCopyLink(url: string) {
  const notifications = useNotifications()
  const { copy } = useCopyToClipboard()
  return useCallback(async () => {
    if (await copy(url)) notifications.success("Link copied to clipboard")
    else notifications.error("Couldn't copy the link. Try again.")
  }, [copy, url, notifications])
}

interface ReportPanelBodyProps {
  deliverableId: string
  fileId?: string
  isExpanded: boolean
  isInline?: boolean
  onClose: () => void
  onName: (name: { deliverableId: string, text: string }) => void
}

export function ReportPanelBody({ deliverableId, fileId, isExpanded, isInline, onClose, onName }: ReportPanelBodyProps) {
  const dispatch = useAppDispatch()
  const viewerUserId = useUserId()
  const isEmailResultsEnabled = useIsScheduledWorkflowEmailResultsEnabled()
  const { data: report, isLoading, error, refetch } = useScheduledWorkflowDeliverable(deliverableId)
  const file = report == null ? undefined : report.files.find((candidate) => candidate.fileId === fileId) ?? primaryFileOf(report.files)
  const navigate = useKellyLinkNavigate()
  const openWorkflow = useCallback(() => {
    if (report != null && navigate("scheduled-workflows", report.workflowId)) return
    dispatch(closeDockPane())
    if (report != null) dispatch(setWorkflowToOpen(report.workflowId))
    onClose()
    // Replaces the address the close just wrote, so the Workflows view is the one entry left after the report.
    navigate("scheduled-workflows", null, { replace: true })
    dispatch(setSurfaceView("workflows"))
  }, [dispatch, onClose, report, navigate])
  const accountAlias = useAccountAlias()
  const copyLink = useCopyLink(reportUrl(accountAlias, deliverableId))
  const { mutate: markRead } = useMarkScheduledWorkflowDeliverableRead(IGNORE_MARK_READ_ERROR)
  const markedDeliverableIds = useRef(new Set<string>())
  const retriedDeliverableIds = useRef(new Set<string>())
  const [markReadAttempt, setMarkReadAttempt] = useState(0)

  useEffect(() => {
    if (report != null) onName({ deliverableId, text: reportEntryLabel(report, viewerTimeZone()) })
  }, [report, deliverableId, onName])

  // every entry point (sidebar, latest-report cell, inline view) renders this body, so it owns mark-read
  useEffect(() => {
    if (report == null || report.read || markedDeliverableIds.current.has(deliverableId)) return
    markedDeliverableIds.current.add(deliverableId)
    markRead(deliverableId, {
      onError: () => {
        if (retriedDeliverableIds.current.has(deliverableId)) return
        retriedDeliverableIds.current.add(deliverableId)
        markedDeliverableIds.current.delete(deliverableId)
        setMarkReadAttempt((attempt) => attempt + 1)
      },
    })
  }, [report, deliverableId, markRead, markReadAttempt])

  const partialWord = isInline ? null : runOutcomeWord(report?.runOutcome ?? null)

  if (isLoading) return <Skeleton variant="rectangular" sx={REPORT_SKELETON_SX} role="progressbar" aria-label="Loading report" data-testid="workflow-report-loading" />
  // no cached report to fall back on: a failed background refetch keeps showing the cached one
  if (report == null && error != null && !(error instanceof NotFoundError)) {
    return (
      <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Try again</Button>}>Couldn&apos;t load this report. Please try again.</Alert>
    )
  }
  if (report == null) return <Alert severity="error">This report is no longer available.</Alert>
  if (file == null) return <Alert severity="info">This report has no files to show.</Alert>

  const otherFiles = report.files.filter((candidate) => candidate.fileId !== file.fileId)
  const access = reportAccessOf(report, viewerUserId)
  const isOwner = access.kind === "owner"
  const source = reportSourceLine(report.workflowName, report.workflowSchedule, viewerTimeZone(), new Date(), access)
  return (
    <>
      {!isInline && (
        <Box sx={CONTEXT_SX}>
          <Typography variant="body2" sx={SOURCE_SX}>
            {source.before}<Box component="strong" sx={WORKFLOW_NAME_SX}>{source.workflowName}</Box>{source.after}
          </Typography>
          {isOwner && <Button size="small" variant="outlined" onClick={openWorkflow}>Open workflow</Button>}
          <Button size="small" variant="text" startIcon={<LinkIcon />} onClick={copyLink}>Copy link</Button>
        </Box>
      )}
      {!isInline && access.kind === "sharee" && isEmailResultsEnabled && <ReportEmailSubscription scheduledWorkflowId={report.workflowId} />}
      {partialWord != null && <Typography variant="body2" sx={PARTIAL_LABEL_SX}>{partialWord}</Typography>}
      <ReportFile deliverableId={deliverableId} file={file} isExpanded={isExpanded} canShare={isOwner} />
      <OtherFiles deliverableId={deliverableId} files={otherFiles} canShare={isOwner} />
    </>
  )
}

interface WorkflowReportViewProps {
  workflow: ScheduledWorkflowListItem
  onBack: () => void
  onOpenReport: (deliverableId: string) => void
  onShare: (workflow: ShareableWorkflow) => void
}

export function WorkflowReportView({ workflow, onBack, onOpenReport, onShare }: WorkflowReportViewProps) {
  const latestDeliverable = workflow.latestDeliverable
  const now = useNow()
  const accountAlias = useAccountAlias()
  const copyLink = useCopyLink(workflowUrl(accountAlias, workflow.scheduledWorkflowId))

  return (
    <Box sx={VIEW_SX} data-testid="workflow-report-view">
      <Box sx={VIEW_HEADER_SX}>
        <Button size="small" variant="text" autoFocus startIcon={<ArrowBack fontSize="small" />} onClick={onBack}>Back</Button>
        <Typography variant="h6" component="h2" sx={VIEW_TITLE_SX}>{workflow.name}</Typography>
        <Button size="small" variant="text" startIcon={<LinkIcon />} onClick={copyLink}>Copy link</Button>
        {workflow.archivedAt == null && <RowActions workflow={workflow} onShare={onShare} />}
      </Box>
      <Box sx={VIEW_BODY_SX}>
        <Box sx={VIEW_REPORT_SX}>
          {latestDeliverable == null
            ? <Typography variant="body2" color="textSecondary">No report yet.</Typography>
            : (
              <>
                <Typography variant="body2" color="textSecondary" sx={LATEST_REPORT_SX}>Latest report · {formatReportTime(latestDeliverable.createdAt, now, viewerTimeZone())}</Typography>
                <ReportPanelBody key={latestDeliverable.deliverableId} deliverableId={latestDeliverable.deliverableId} isExpanded isInline onClose={IGNORE} onName={IGNORE} />
              </>
            )}
        </Box>
        <Box sx={VIEW_RUNS_SX}>
          <RunHistory workflowId={workflow.scheduledWorkflowId} onOpenReport={onOpenReport} />
        </Box>
      </Box>
    </Box>
  )
}
